import type {
  AuditDetail,
  AuditSummary,
  CardPage,
  ChatMessage,
  ReviewRow,
  ReviewSummary,
  StageSummary,
  UnitState,
} from '../types.ts'
import type { AuditsApi } from './audits-api.types.ts'

/**
 * The mock (#240): both pages render without an engine, and every audit
 * state has a row here so every badge is exercised.
 */
const day = (d: number, h = 0): Date => new Date(Date.UTC(2026, 6, d, h))

const quota = { asr_minutes: 600, llm_tokens: 2_000_000, amount: 15_000, currency: 'EUR' }

const spend = (amount: number, units: number) => ({
  spent: { asr_minutes: units * 3, llm_tokens: units * 4_000, amount, calls: units },
  quota,
  exhausted: amount >= quota.amount ? ('amount' as const) : null,
  units,
  perUnit: units === 0 ? 0 : Math.round(amount / units),
})

function summary(
  id: string,
  title: string,
  state: AuditSummary['state'],
  extra: Partial<AuditSummary> = {},
): AuditSummary {
  return {
    id,
    title,
    pipelineIds: ['pl-deals', 'pl-calls'],
    periodFrom: day(1),
    periodTo: new Date(Date.UTC(2026, 7, 1)),
    cutoffAt: new Date(Date.UTC(2026, 7, 1)),
    snapshotAt:
      state === 'draft' || state === 'snapshotting' ? null : new Date(Date.UTC(2026, 7, 1, 12)),
    state,
    quota,
    createdAt: new Date(Date.UTC(2026, 7, 1, 11)),
    updatedAt: new Date(Date.UTC(2026, 7, 1, 12)),
    spend: state === 'deleted' ? null : spend(0, 0),
    anomalies: state === 'deleted' ? null : 0,
    ...extra,
  }
}

const AUDITS: AuditSummary[] = [
  summary('aud-july', 'July 2026 — NSA', 'processing', { spend: spend(6_200, 410), anomalies: 37 }),
  summary('aud-june', 'June 2026 — NSA', 'ready', { spend: spend(9_800, 620), anomalies: 4 }),
  summary('aud-draft', 'August 2026 — draft', 'draft', {
    periodFrom: new Date(Date.UTC(2026, 7, 1)),
    periodTo: new Date(Date.UTC(2026, 8, 1)),
    cutoffAt: new Date(Date.UTC(2026, 8, 1)),
  }),
  summary('aud-snap', 'May 2026 — snapshot', 'snapshotting'),
  summary('aud-paused', 'April 2026 — paused', 'paused_quota', {
    spend: spend(15_000, 700),
    anomalies: 12,
  }),
  summary('aud-failed', 'March 2026 — failed', 'failed'),
  summary('aud-archived', 'February 2026', 'archived', { spend: spend(8_000, 500), anomalies: 0 }),
  summary('aud-deleted', 'January 2026', 'deleted', { spend: null, anomalies: null }),
]

const unitsOf = (done: number, total: number, rest: Partial<Record<UnitState, number>> = {}) => ({
  total,
  queued: 0,
  running: 0,
  done,
  failed: 0,
  skipped: 0,
  needs_review: 0,
  ...rest,
})

const STAGES: Record<string, StageSummary[]> = {
  'aud-july': [
    {
      stage: 'snapshot',
      level: 'audit',
      enabled: true,
      job: { state: 'done', updatedAt: day(31) },
      units: unitsOf(0, 0),
      skipped: {},
    },
    {
      stage: 'transcribe',
      level: 'card',
      enabled: true,
      job: { state: 'done', updatedAt: day(31) },
      units: unitsOf(380, 410, { skipped: 26, failed: 4 }),
      skipped: { no_audio: 26 },
    },
    {
      stage: 'pseudonymize',
      level: 'card',
      enabled: true,
      job: { state: 'done', updatedAt: day(31) },
      units: unitsOf(380, 410, { skipped: 30 }),
      skipped: { no_transcript: 30 },
    },
    {
      stage: 'classify_call',
      level: 'card',
      enabled: true,
      job: { state: 'running', updatedAt: day(31) },
      units: unitsOf(210, 410, { queued: 150, skipped: 30, needs_review: 20 }),
      skipped: { no_transcript: 30 },
    },
    {
      stage: 'extract_facts',
      level: 'card',
      enabled: true,
      job: null,
      units: unitsOf(0, 0),
      skipped: {},
    },
    {
      stage: 'link',
      level: 'card',
      enabled: true,
      job: { state: 'done', updatedAt: day(31) },
      units: unitsOf(1_260, 1_260),
      skipped: {},
    },
    {
      stage: 'qa_checklist',
      level: 'card',
      enabled: true,
      job: null,
      units: unitsOf(0, 0),
      skipped: {},
    },
    {
      stage: 'segment',
      level: 'card',
      enabled: true,
      job: null,
      units: unitsOf(0, 0),
      skipped: {},
    },
    { stage: 'marts', level: 'audit', enabled: true, job: null, units: unitsOf(0, 0), skipped: {} },
    {
      stage: 'export',
      level: 'audit',
      enabled: false,
      job: null,
      units: unitsOf(0, 0),
      skipped: {},
    },
  ],
}

const REVIEWS: Record<string, ReviewSummary[]> = {
  'aud-july': [
    ...Array.from({ length: 21 }, (_, i) => ({
      id: `q-mc-${i}`,
      kind: 'multi_candidate',
      state: 'open' as const,
      leadId: `call-${i}`,
    })),
    ...Array.from({ length: 9 }, (_, i) => ({
      id: `q-co-${i}`,
      kind: 'call_only_lead',
      state: 'open' as const,
      leadId: `call-x${i}`,
    })),
    ...Array.from({ length: 5 }, (_, i) => ({
      id: `q-ei-${i}`,
      kind: 'empty_identity',
      state: 'open' as const,
      leadId: `deal-${i}`,
    })),
    ...Array.from({ length: 2 }, (_, i) => ({
      id: `q-us-${i}`,
      kind: 'unsupported_stage',
      state: 'open' as const,
      leadId: `deal-s${i}`,
    })),
  ],
}

function detailOf(audit: AuditSummary): AuditDetail {
  return {
    audit,
    stagesEnabled: [
      'snapshot',
      'transcribe',
      'pseudonymize',
      'classify_call',
      'extract_facts',
      'link',
      'qa_checklist',
      'segment',
      'marts',
    ],
    providers: { asr: 'runpod-whisperx', llm: 'anthropic', llm_model: 'claude-sonnet-5' },
    counters: {
      cardsPerPipeline: { 'pl-deals': 850, 'pl-calls': 410 },
      calls: { withAudio: 384, withoutAudio: 26 },
      contacts: { withPhone: 790, withoutPhone: 60 },
    },
    coverage:
      audit.state === 'draft'
        ? {
            covered: false,
            missing: [{ pipelineId: 'pl-calls', from: audit.periodFrom, to: audit.periodTo }],
          }
        : { covered: true, missing: [] },
    spend: audit.spend ?? spend(0, 0),
    blockedBy: audit.state === 'processing' ? ['link_coverage'] : [],
    log: [
      {
        id: 'l-1',
        actor: 'user',
        action: 'created',
        subject: audit.id,
        details: {},
        at: audit.createdAt,
      },
      {
        id: 'l-2',
        actor: 'system',
        action: 'state_changed',
        subject: audit.state,
        details: { to: audit.state },
        at: audit.updatedAt,
      },
    ],
  }
}

const delay = <T>(value: T, ms = 120): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), ms))

const CARD: CardPage = {
  card: {
    id: 'call-1',
    pipelineId: 'pl-calls',
    pipelineName: 'Calls (Ringostat)',
    kind: 'interaction',
    name: 'Incoming call from +39 347 000 0001',
    statusName: 'Call',
    lifecyclePhase: null,
    assignedManagerId: 'm-giulia',
    funnel: null,
    tags: ['inbound'],
    createdAt: day(2, 11),
    ludusLeadId: 'cycle-a',
    link: { confidence: 'high', method: 'window', linkedBy: 'auto' },
  },
  stages: [
    { stage: 'transcribe', state: 'done', reason: null },
    { stage: 'pseudonymize', state: 'done', reason: null },
    { stage: 'classify_call', state: 'done', reason: null },
    { stage: 'extract_facts', state: 'done', reason: null },
    { stage: 'link', state: 'done', reason: null },
    { stage: 'qa_checklist', state: 'done', reason: null },
    { stage: 'segment', state: 'queued', reason: null },
  ],
  transcript: {
    id: 'tr-1',
    language: 'it',
    durationMs: 60000,
    roleConfidence: 90,
    segments: [
      {
        idx: 0,
        startMs: 0,
        endMs: 10000,
        speaker: 'SPEAKER_00',
        role: 'manager',
        text: 'NSA buongiorno, sono Giulia.',
      },
      {
        idx: 1,
        startMs: 10000,
        endMs: 30000,
        speaker: 'SPEAKER_01',
        role: 'customer',
        text: 'Salve, sono Anna Rossi. Vorrei il corso base, ho un budget di 500 euro.',
      },
      {
        idx: 2,
        startMs: 30000,
        endMs: 50000,
        speaker: 'SPEAKER_00',
        role: 'manager',
        text: 'Le propongo il corso base a 400 euro con acconto.',
      },
      {
        idx: 3,
        startMs: 50000,
        endMs: 60000,
        speaker: 'SPEAKER_01',
        role: 'customer',
        text: 'Va bene, ci vediamo martedì alle 15.',
      },
    ],
  },
  audio: [{ id: 'att-1', mime: 'audio/mpeg', durationMs: 60000, url: null, erasedAt: null }],
  facts: [
    {
      id: 'f-1',
      key: 'call_type',
      value: 'first_contact',
      kind: 'inference',
      confidence: 90,
      stage: 'classify_call',
      reviewState: 'unreviewed',
      evidence: [
        {
          id: 'e-1',
          kind: 'transcript_span',
          refId: 'tr-1',
          startMs: 0,
          endMs: 60000,
          quote: 'the customer asks about the course',
        },
      ],
    },
    {
      id: 'f-2',
      key: 'need',
      value: 'corso base',
      kind: 'fact',
      confidence: 90,
      stage: 'extract_facts',
      reviewState: 'unreviewed',
      evidence: [
        {
          id: 'e-2',
          kind: 'transcript_span',
          refId: 'tr-1',
          startMs: 10000,
          endMs: 30000,
          quote: 'Vorrei il corso base',
        },
      ],
    },
    {
      id: 'f-3',
      key: 'budget',
      value: '500 euro',
      kind: 'fact',
      confidence: 80,
      stage: 'extract_facts',
      reviewState: 'unreviewed',
      evidence: [
        {
          id: 'e-3',
          kind: 'transcript_span',
          refId: 'tr-1',
          startMs: 10000,
          endMs: 30000,
          quote: 'ho un budget di 500 euro',
        },
      ],
    },
    {
      id: 'f-4',
      key: 'decision_maker',
      value: 'il cliente stesso',
      kind: 'hypothesis',
      confidence: 60,
      stage: 'extract_facts',
      reviewState: 'unreviewed',
      evidence: [],
    },
    {
      id: 'f-5',
      key: 'next_step',
      value: 'visita martedì alle 15',
      kind: 'fact',
      confidence: 85,
      stage: 'extract_facts',
      reviewState: 'unreviewed',
      evidence: [
        {
          id: 'e-5',
          kind: 'transcript_span',
          refId: 'tr-1',
          startMs: 50000,
          endMs: 60000,
          quote: 'ci vediamo martedì alle 15',
        },
      ],
    },
  ],
  qa: [
    {
      phase: 'greeting',
      applicability: 'applicable',
      coverage: 'offered',
      fidelityScore: 90,
      explanation: '[00:02] names the school and herself',
    },
    {
      phase: 'needs_discovery',
      applicability: 'applicable',
      coverage: 'partial',
      fidelityScore: 50,
      explanation: '[00:12] the customer volunteers the need',
    },
    {
      phase: 'presentation',
      applicability: 'applicable',
      coverage: 'offered',
      fidelityScore: 80,
      explanation: '[00:31] the course and the price',
    },
    {
      phase: 'objections',
      applicability: 'n_a',
      coverage: 'not_offered',
      fidelityScore: null,
      explanation: '[00:50] no objection was raised',
    },
    {
      phase: 'closing',
      applicability: 'applicable',
      coverage: 'offered',
      fidelityScore: 90,
      explanation: '[00:52] a visit on Tuesday at 15',
    },
  ],
  stageEvidence: [],
  identity: {
    cycle: {
      id: 'cycle-a',
      origin: 'deal',
      productKey: 'corso-base',
      anomalyFlags: ['multi_candidate'],
    },
    customer: { id: 'cust-a', anomaly: 'none' },
    siblings: [
      {
        id: 'deal-a',
        kind: 'deal',
        name: 'Anna Rossi — corso base',
        createdAt: day(1),
        linkConfidence: 'high',
      },
    ],
  },
  history: [],
}

const DEAL: CardPage = {
  ...CARD,
  card: {
    ...CARD.card,
    id: 'deal-a',
    pipelineId: 'pl-deals',
    pipelineName: 'Vendite (Kommo)',
    kind: 'deal',
    name: 'Anna Rossi — corso base',
    statusName: 'Won',
    lifecyclePhase: 'won',
    createdAt: day(1),
  },
  stages: [
    { stage: 'link', state: 'done', reason: null },
    { stage: 'extract_facts', state: 'skipped', reason: 'no_communications' },
    { stage: 'qa_checklist', state: 'done', reason: null },
  ],
  transcript: null,
  audio: [],
  facts: [],
  qa: [],
  stageEvidence: [
    {
      id: 'se-1',
      statusName: 'Proposal',
      occurredAt: day(2),
      verdict: 'supported',
      explanation: 'the course and the price were discussed',
      missing: null,
    },
    {
      id: 'se-2',
      statusName: 'Won',
      occurredAt: day(3),
      verdict: 'unsupported',
      explanation: 'nothing shows an enrolment or a payment',
      missing: 'a payment or an enrolment confirmed in a call',
    },
  ],
  identity: {
    ...CARD.identity,
    siblings: [
      {
        id: 'call-1',
        kind: 'interaction',
        name: 'Incoming call from +39 347 000 0001',
        createdAt: day(2, 11),
        linkConfidence: 'high',
      },
    ],
  },
  history: [
    { id: 'h-1', at: day(2), from: 'New', to: 'Proposal', kind: 'status_changed' },
    { id: 'h-2', at: day(3), from: 'Proposal', to: 'Won', kind: 'status_changed' },
  ],
}

const QUEUE: ReviewRow[] = [
  {
    id: 'q-mc-1',
    kind: 'multi_candidate',
    subjectType: 'lead',
    subjectId: 'call-1',
    leadId: 'call-1',
    proposal: { action: 'confirm_link', leadId: 'call-1', ludusLeadId: 'cycle-a' },
    evidence: { candidates: 2, winnerDistanceDays: 1, runnerUpDistanceDays: 6 },
    state: 'open',
    resolution: null,
    resolvedBy: null,
    reason: null,
    ruleVersion: 'abc',
    createdAt: day(31),
    confidence: 0.83,
  },
  {
    id: 'q-mc-2',
    kind: 'multi_candidate',
    subjectType: 'lead',
    subjectId: 'call-9',
    leadId: 'call-9',
    proposal: { action: 'confirm_link', leadId: 'call-9', ludusLeadId: 'cycle-b' },
    evidence: { candidates: 2, winnerDistanceDays: 3, runnerUpDistanceDays: 3.2 },
    state: 'open',
    resolution: null,
    resolvedBy: null,
    reason: null,
    ruleVersion: 'abc',
    createdAt: day(31),
    confidence: 0.06,
  },
  {
    id: 'q-co-1',
    kind: 'call_only_lead',
    subjectType: 'lead',
    subjectId: 'call-x',
    leadId: 'call-x',
    proposal: { action: 'confirm_link', leadId: 'call-x', ludusLeadId: 'cycle-x' },
    evidence: { rule: 'audit_queues_call_only' },
    state: 'open',
    resolution: null,
    resolvedBy: null,
    reason: null,
    ruleVersion: 'abc',
    createdAt: day(31),
    confidence: null,
  },
  {
    id: 'q-ei-1',
    kind: 'empty_identity',
    subjectType: 'lead',
    subjectId: 'deal-e',
    leadId: 'deal-e',
    proposal: { action: 'confirm_customer', customerId: 'cust-e' },
    evidence: {},
    state: 'resolved',
    resolution: 'accepted',
    resolvedBy: 'sem',
    reason: 'a walk-in',
    ruleVersion: 'abc',
    createdAt: day(30),
    confidence: 0,
  },
]

const CHAT: ChatMessage[] = [
  {
    id: 'm-1',
    role: 'user',
    text: 'What did the customer ask for in the call on the second of July?',
    evidence: [],
    inReplyTo: null,
    polledAt: day(31, 9),
    answeredAt: day(31, 9),
    createdAt: day(31, 8),
  },
  {
    id: 'm-2',
    role: 'assistant',
    text: 'The customer asked for the basic course and named a budget of 500 euro (00:10–00:30).',
    evidence: [
      { kind: 'call', ref_id: 'call-1', start_ms: 10000, end_ms: 30000 },
      { kind: 'fact', ref_id: 'f-3' },
    ],
    inReplyTo: 'm-1',
    polledAt: null,
    answeredAt: null,
    createdAt: day(31, 9),
  },
]

export const auditsApiMock: AuditsApi = {
  card: (_auditId, cardId) =>
    delay(
      cardId === 'deal-a'
        ? { success: true, data: DEAL }
        : cardId === 'call-1'
          ? { success: true, data: CARD }
          : { success: false, error: { kind: 'not_found', status: 404, message: 'not found' } },
    ),
  reviewQueue: () =>
    delay({
      success: true,
      data: { items: QUEUE, budget: { hours: 12, spentHours: 1.5, decisions: 1 } },
    }),
  decide: (_auditId, queueId, input) => {
    const row = QUEUE.find((r) => r.id === queueId)
    if (!row)
      return delay({
        success: false,
        error: { kind: 'not_found', status: 404, message: 'not found' },
      })
    if (input.reason.trim() === '')
      return delay({
        success: false,
        error: {
          kind: 'conflict',
          status: 409,
          message: 'a decision needs a reason',
          code: 'review_input',
        },
      })
    Object.assign(row, {
      state: 'resolved',
      resolution: input.decision === 'accept' ? 'accepted' : 'rejected',
      resolvedBy: 'console',
      reason: input.reason,
    })
    return delay({ success: true, data: row })
  },
  chat: () => delay({ success: true, data: CHAT }),
  ask: (_auditId, text) => {
    const message: ChatMessage = {
      id: `m-${Date.now()}`,
      role: 'user',
      text,
      evidence: [],
      inReplyTo: null,
      polledAt: null,
      answeredAt: null,
      createdAt: new Date(),
    }
    CHAT.push(message)
    return delay({ success: true, data: message })
  },
  brain: () => delay({ success: true, data: { online: false, lastPollAt: day(31, 9), polls: 12 } }),
  list: () => delay({ success: true, data: AUDITS }),
  get: (auditId) => {
    const audit = AUDITS.find((a) => a.id === auditId)
    return delay(
      audit
        ? { success: true, data: detailOf(audit) }
        : { success: false, error: { kind: 'not_found', status: 404, message: 'not found' } },
    )
  },
  create: (input) => {
    const created = summary(`aud-${Date.now()}`, input.title, 'draft', {
      pipelineIds: input.pipelineIds,
      periodFrom: input.periodFrom,
      periodTo: input.periodTo,
      cutoffAt: input.cutoffAt ?? input.periodTo,
    })
    AUDITS.unshift(created)
    return delay({ success: true, data: created })
  },
  stages: (auditId) =>
    delay({
      success: true,
      data:
        STAGES[auditId] ??
        STAGES['aud-july']!.map((s) => ({ ...s, job: null, units: unitsOf(0, 0), skipped: {} })),
    }),
  runStage: () => delay({ success: true, data: { jobId: `job-${Date.now()}` } }),
  patchQuota: (auditId, patch) => {
    const audit = AUDITS.find((a) => a.id === auditId)
    if (!audit)
      return delay({
        success: false,
        error: { kind: 'not_found', status: 404, message: 'not found' },
      })
    audit.quota = { ...audit.quota, ...patch }
    if (audit.state === 'paused_quota') audit.state = 'processing'
    return delay({ success: true, data: audit })
  },
  openReviews: (auditId) => delay({ success: true, data: REVIEWS[auditId] ?? [] }),
  pipelines: () =>
    delay({
      success: true,
      data: [
        { id: 'pl-deals', name: 'Vendite (Kommo)', kind: 'deal' },
        { id: 'pl-calls', name: 'Calls (Ringostat)', kind: 'interaction' },
      ],
    }),
  backfill: (pipelineId) =>
    delay({
      success: true,
      data:
        pipelineId === 'pl-calls'
          ? {
              job: { state: 'running', pages: 7, error: null },
              backfilledFrom: null,
              backfilledTo: null,
            }
          : { job: null, backfilledFrom: day(1), backfilledTo: new Date(Date.UTC(2026, 7, 1)) },
    }),
}
